import React, { useEffect, useState } from "react";
import { Alert, Col, Form, message, Row, Skeleton } from "antd";
import {
  CancelButton,
  InputDate,
  InputText,
  InputTextArea,
  ModalComponent,
  SaveButton,
  SelectWithSearch,
  UploadFile,
} from "@comps/components";
import { createSelectList } from "@comps/commonFunctions";
import { getErrorProps, makeRequest, makeRequestStateless } from "@utils/helpers";
import { createDocumentReq, getDocumentTypesReq } from "../requests";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { employeeTooltips } from "../../common/employeeTooltips";

const formName = "createEmploymentDocument";
const fieldLabel = makeFieldLabel(employeeTooltips.document, { namespace: "document" });
const acceptedFiles = ".pdf,.jpg,.png,.jpeg,.doc,.docx";

const CreateDocument = (props) => {
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    makeRequestStateless(getDocumentTypesReq, false, onDocumentTypesLoaded, onDocumentTypesError);
  }, []);

  const onDocumentTypesLoaded = (response) => {
    const data = Object.values(response.resources?.data || {});
    setDocumentTypes(createSelectList(data));
    setLoading(false);
  };

  const onDocumentTypesError = () => {
    setDocumentTypes([]);
    setLoading(false);
  };

  const onSubmit = (data) => {
    if (!documentTypes.length) {
      message.warning("Create a document type before uploading employee documents.");
      return;
    }
    if (!fileList.length) {
      message.warning("Attachment is required.");
      return;
    }

    setSaving(true);
    const payload = {
      my_document: JSON.stringify({
        name: data.name,
        document_type_id: data.document_type_id,
        expiry_date: data.expiry_date ? data.expiry_date.format("YYYY-MM-DD") : null,
        additional_info: data.additional_info || null,
      }),
      file_attachment: fileList.length > 0 && fileList[0].id === undefined ? fileList[0].originFileObj : null,
      employmentId: props.employmentId,
      filters: props.filters,
    };
    makeRequest(setLoader, createDocumentReq, payload, onSuccess, onError);
  };

  const onSuccess = (data) => {
    setSaving(false);
    message.success("Document uploaded successfully.");
    props.onCompleted(data.my_documents.data);
    props.onSuccess(data);
  };

  const onError = (err, res) => {
    setSaving(false);
    message.error(res?.response?.data?.message || "Document could not be uploaded.");
    setErrors(err || []);
  };

  const footer = [
    <SaveButton loading={ saving || loader } form={ formName } key="create_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />,
  ];

  return (
    <ModalComponent
      top={ 20 }
      width={ 900 }
      maskClosable={ false }
      mainTitle="Upload Document"
      subTitle="Employee Documents"
      visible={ true }
      footer={ footer }
      onCancel={ () => props.onCompleted(false) }
    >
      <Skeleton loading={ loading }>
        { !documentTypes.length ? (
          <Alert
            className="da-mb-16"
            type="warning"
            showIcon
            message="Create a document type before uploading employee documents."
          />
        ) : null }
        <Form { ...formLayout } name={ formName } onFinish={ onSubmit } scrollToFirstError>
          <Row gutter={ 30 }>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="name" rules={ rules.name } label={ fieldLabel("name", "Document Name") } { ...getErrorProps(errors["name"]) }>
                <InputText placeholder="e.g. Warning Letter - July 2026" />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item
                name="document_type_id"
                rules={ rules.document_type_id }
                label={ fieldLabel("document_type_id", "Document Type") }
                { ...getErrorProps(errors["document_type_id"]) }
              >
                <SelectWithSearch options={ documentTypes } placeholder="Select document type" disabled={ !documentTypes.length } />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="expiry_date" label={ fieldLabel("expiry_date", "Expiry Date") }>
                <InputDate format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="file_attachment" label={ fieldLabel("file_attachment", "Attachment") }>
                <UploadFile fileList={ fileList } setFileList={ setFileList } accept={ acceptedFiles } />
              </Form.Item>
            </Col>
            <Col span={ 24 }>
              <Form.Item name="additional_info" label={ fieldLabel("additional_info", "Additional Info") }>
                <InputTextArea placeholder="Optional notes for HR or the employee" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </ModalComponent>
  );
};

export default CreateDocument;

const rules = {
  name: [{ required: true, message: "Document name is required." }],
  document_type_id: [{ required: true, message: "Document type is required." }],
};

const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left",
};
